// Task outline: hierarchical index numbers + ordering + moves, the same model as
// BattleBids estimate items (battlebids-web/src/lib/estimating/tree.ts).
//
//   - Hierarchy is `parentId` (any depth). Order is `sortOrder` among siblings,
//     gapped by SORT_GAP so a move is normally ONE write (the midpoint of its new
//     neighbours); the siblings are renumbered only when a gap runs out.
//   - Index labels ("2", "2.3", "2.3.1") are DERIVED, never stored — reordering
//     renumbers everything for free.
//   - A project's sections are its phases: section N (by `order`) is the leading
//     number. Unsectioned root tasks are phase 0 ("0.1", "0.2") when the project
//     has sections, plain "1", "2" when it has none.
//   - Siblings of a root task = the root tasks in the same section.
//
// Pure: no I/O. Reused by mobile + web.

export const SORT_GAP = 1000;

function orderVal(t) {
  const so = t.sortOrder;
  return (so === undefined || so === null) ? (t.createdAt || 0) : so;
}

/** Sibling order: sortOrder (legacy: createdAt), then createdAt, then title. */
export function bySort(a, b) {
  return (orderVal(a) - orderVal(b))
    || ((a.createdAt || 0) - (b.createdAt || 0))
    || String(a.title || '').localeCompare(String(b.title || ''));
}

function sortedSections(sections) {
  return [...(sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
}

/** The ordered sibling list `task` belongs to (including itself). */
export function siblingsOf(allTasks, task) {
  const tasks = allTasks.filter((t) => !t.bucket);
  const list = task.parentId
    ? tasks.filter((t) => t.parentId === task.parentId)
    : tasks.filter((t) => !t.parentId && (t.sectionId || null) === (task.sectionId || null));
  return list.sort(bySort);
}

/**
 * Flatten one project's tasks into outline order.
 * @returns {Array<{ task, depth, label, sectionId, childCount }>} depth 0 = root task.
 *   Tasks whose parent is missing are treated as roots (never dropped).
 */
export function buildTaskOutline(allTasks = [], sections = []) {
  // Setup / cleanup checklist items (bucket tasks) are not steps of the job: no number, no row.
  const tasks = allTasks.filter((t) => !t.bucket);
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const kids = new Map();
  const roots = [];
  for (const t of tasks) {
    if (t.parentId && byId.has(t.parentId) && t.parentId !== t.id) {
      if (!kids.has(t.parentId)) kids.set(t.parentId, []);
      kids.get(t.parentId).push(t);
    } else {
      roots.push(t);
    }
  }
  const secs = sortedSections(sections);
  const hasSections = secs.length > 0;
  const known = new Set(secs.map((s) => s.id));
  const groups = [{ id: null, phase: hasSections ? '0' : null }];
  secs.forEach((s, i) => groups.push({ id: s.id, phase: String(i + 1) }));

  const rows = [];
  const seen = new Set();
  const walk = (list, prefix, depth, sectionId) => {
    list.sort(bySort).forEach((t, i) => {
      if (seen.has(t.id)) return;        // cycle guard
      seen.add(t.id);
      const label = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
      const children = kids.get(t.id) || [];
      rows.push({ task: t, depth, label, sectionId, childCount: children.length });
      walk([...children], label, depth + 1, sectionId);
    });
  };
  for (const g of groups) {
    const list = roots.filter((t) => (g.id === null
      ? !t.sectionId || !known.has(t.sectionId)
      : t.sectionId === g.id));
    walk(list, g.phase, 0, g.id);
  }
  return rows;
}

/** taskId → index label for one project ("1.2", "1.2.1", …). */
export function computeTaskIndices(tasks = [], sections = []) {
  const m = new Map();
  for (const r of buildTaskOutline(tasks, sections)) m.set(r.task.id, r.label);
  return m;
}

/** A sortOrder strictly between two neighbours (either may be absent), or null
 *  when the gap is used up and the siblings need renumbering. */
export function sortOrderBetween(before, after) {
  if (before == null && after == null) return SORT_GAP;
  if (before == null) return after - SORT_GAP;
  if (after == null) return before + SORT_GAP;
  if (after - before <= 1) return null;
  return Math.floor((before + after) / 2);
}

/**
 * Where a new task goes when it is added "here", on the card you are working: it takes that card's
 * place in the outline (same project, phase and parent, the sortOrder just before it), and the card
 * moves down one. Renumbers the siblings only when the gap is used up.
 * @returns {{ fields: { projectId, sectionId, parentId, sortOrder }, writes: Array<{ id, patch }> } | null}
 */
export function planInsertBefore(tasks = [], anchorId) {
  const anchor = tasks.find((t) => t.id === anchorId);
  if (!anchor) return null;
  const sibs = siblingsOf(tasks, anchor);
  const at = sibs.findIndex((t) => t.id === anchorId);
  const before = sibs[at - 1];
  let sortOrder = sortOrderBetween(before ? orderVal(before) : null, orderVal(anchor));
  const writes = [];
  if (sortOrder === null) {
    sibs.forEach((t, k) => writes.push({ id: t.id, patch: { sortOrder: (k + (k >= at ? 2 : 1)) * SORT_GAP } }));
    sortOrder = (at + 1) * SORT_GAP;
  }
  return { fields: { projectId: anchor.projectId || null, sectionId: anchor.sectionId || null, parentId: anchor.parentId || null, sortOrder }, writes };
}

function descendants(tasks, id) {
  const out = [];
  const stack = [id];
  const seen = new Set([id]);
  while (stack.length) {
    const p = stack.pop();
    for (const t of tasks) {
      if (t.parentId === p && !seen.has(t.id)) { seen.add(t.id); out.push(t); stack.push(t.id); }
    }
  }
  return out;
}

/**
 * Plan a move of one task within its project.
 *   dir 'up' | 'down'   — swap with the neighbouring sibling. A ROOT task at the edge
 *                          of its section crosses into the previous / next section.
 *   dir 'indent'        — become the last child of the sibling above.
 *   dir 'outdent'       — leave its parent; land directly after it.
 * @returns {Array<{ id, patch }>} the writes (empty = nothing to do). Descendants of a
 *   task that changes section follow it (sectionId only).
 */
export function planTaskMove(tasks = [], sections = [], id, dir) {
  const task = tasks.find((t) => t.id === id);
  if (!task) return [];
  const sibs = siblingsOf(tasks, task);
  const i = sibs.findIndex((t) => t.id === id);
  const writes = new Map();
  const put = (tid, patch) => writes.set(tid, { ...(writes.get(tid) || {}), ...patch });

  // Place `task` into the ordered list `list` (task not in it) at position `at`.
  const place = (list, at, fields) => {
    const before = list[at - 1];
    const after = list[at];
    let so = sortOrderBetween(before ? orderVal(before) : null, after ? orderVal(after) : null);
    if (so === null) {
      list.forEach((t, k) => put(t.id, { sortOrder: (k + (k >= at ? 2 : 1)) * SORT_GAP }));
      so = (at + 1) * SORT_GAP;
    }
    put(task.id, { ...fields, sortOrder: so });
  };
  const moveSection = (sectionId) => {
    if ((task.sectionId || null) === (sectionId || null)) return;
    for (const d of descendants(tasks, task.id)) put(d.id, { sectionId: sectionId || null });
  };

  if (dir === 'up' || dir === 'down') {
    const j = dir === 'up' ? i - 1 : i + 1;
    const rest = sibs.filter((t) => t.id !== id);
    if (j >= 0 && j < sibs.length) {
      place(rest, dir === 'up' ? i - 1 : i + 1, {});
    } else if (!task.parentId) {
      const order = [null, ...sortedSections(sections).map((s) => s.id)];
      const k = order.indexOf(task.sectionId || null) + (dir === 'up' ? -1 : 1);
      if (k < 0 || k >= order.length) return [];
      const target = order[k];
      const list = tasks.filter((t) => !t.parentId && t.id !== id && (t.sectionId || null) === target).sort(bySort);
      place(list, dir === 'up' ? list.length : 0, { sectionId: target });
      moveSection(target);
    } else {
      return [];
    }
  } else if (dir === 'indent') {
    if (i <= 0) return [];
    const parent = sibs[i - 1];
    const list = tasks.filter((t) => t.parentId === parent.id).sort(bySort);
    place(list, list.length, { parentId: parent.id, sectionId: parent.sectionId || null });
    moveSection(parent.sectionId || null);
  } else if (dir === 'outdent') {
    const parent = tasks.find((t) => t.id === task.parentId);
    if (!parent) return [];
    const list = siblingsOf(tasks, parent).filter((t) => t.id !== id);
    const at = list.findIndex((t) => t.id === parent.id) + 1;
    place(list, at, { parentId: parent.parentId || null, sectionId: parent.sectionId || null });
    moveSection(parent.sectionId || null);
  } else {
    return [];
  }
  return [...writes.entries()].map(([tid, patch]) => ({ id: tid, patch }));
}

/**
 * Plan a drag-and-drop move. `rows` = the outline as shown WITHOUT the dragged task and its subtree
 * (buildTaskOutline rows, optionally with `{ header: true, sectionId }` phase headers between them).
 * `at` = where it was dropped (0..rows.length: before rows[at]); `depth` = the depth the finger asked for
 * (sideways drag), clamped to what's possible there: 0 … the row above's depth + 1.
 *   parent  = the nearest row above at depth − 1 (never across a phase header)
 *   phase   = the phase of the row above (or the header it sits under); a child takes its parent's
 *   slot    = right after the nearest sibling above, else first — gapped sortOrder, one write normally.
 * Descendants follow a phase change. @returns {Array<{ id, patch }>} (empty = no change).
 */
export function planTaskDrop(tasks = [], sections = [], id, rows = [], at = 0, depth = null) {
  const task = tasks.find((t) => t.id === id);
  if (!task) return [];
  const pos = Math.max(0, Math.min(rows.length, at));
  const above = rows[pos - 1] || null;
  const below = rows[pos] || null;
  const own = (r) => (r.header ? (r.sectionId ?? null) : (r.task.sectionId ?? r.sectionId ?? null));
  let sectionId = above ? own(above) : below ? own(below) : (task.sectionId ?? null);
  const maxD = above && !above.header ? above.depth + 1 : 0;
  let d = Math.max(0, Math.min(maxD, depth == null ? 0 : Math.round(depth)));

  let parent = null;
  if (d > 0) {
    for (let k = pos - 1; k >= 0; k--) {
      const r = rows[k];
      if (r.header) break;
      if (r.depth === d - 1) { parent = r.task; break; }
      if (r.depth < d - 1) break;
    }
    if (!parent) d = 0;
  }
  if (parent) sectionId = parent.sectionId ?? null;
  let prevId = null;
  for (let k = pos - 1; k >= 0; k--) {
    const r = rows[k];
    if (r.header || r.depth < d) break;
    if (r.depth === d) { prevId = r.task.id; break; }
  }

  const parentId = parent ? parent.id : null;
  const sibs = tasks.filter((t) => t.id !== id && (t.parentId || null) === parentId
    && (parentId ? true : (t.sectionId || null) === (sectionId || null))).sort(bySort);
  const idx = prevId ? sibs.findIndex((t) => t.id === prevId) + 1 : 0;
  const before = sibs[idx - 1];
  const after = sibs[idx];
  const sameSpot = (task.parentId || null) === parentId && (task.sectionId || null) === (sectionId || null)
    && sibs.indexOf(after) === siblingsOf(tasks, task).filter((t) => t.id !== id).indexOf(after)
    && siblingsOf(tasks, task).findIndex((t) => t.id === id) === idx;
  if (sameSpot) return [];

  const writes = new Map();
  const put = (tid, patch) => writes.set(tid, { ...(writes.get(tid) || {}), ...patch });
  let so = sortOrderBetween(before ? orderVal(before) : null, after ? orderVal(after) : null);
  if (so === null) {
    sibs.forEach((t, k) => put(t.id, { sortOrder: (k + (k >= idx ? 2 : 1)) * SORT_GAP }));
    so = (idx + 1) * SORT_GAP;
  }
  put(task.id, { parentId, sectionId: sectionId || null, sortOrder: so });
  if ((task.sectionId || null) !== (sectionId || null)) for (const x of descendants(tasks, task.id)) put(x.id, { sectionId: sectionId || null });
  return [...writes.entries()].map(([tid, patch]) => ({ id: tid, patch }));
}

/** The outline rows a drag works against: everything except the dragged task and its subtree. */
export function outlineWithout(rows = [], id) {
  const gone = new Set([id]);
  const out = [];
  for (const r of rows) {
    if (!r.header && (gone.has(r.task.id) || (r.task.parentId && gone.has(r.task.parentId)))) { gone.add(r.task.id); continue; }
    out.push(r);
  }
  return out;
}

/** sortOrder for a new task appended to the end of its sibling list. */
export function nextSortOrder(tasks = [], { parentId = null, sectionId = null } = {}) {
  const sibs = parentId
    ? tasks.filter((t) => t.parentId === parentId)
    : tasks.filter((t) => !t.parentId && (t.sectionId || null) === (sectionId || null));
  if (!sibs.length) return SORT_GAP;
  return Math.max(...sibs.map(orderVal)) + SORT_GAP;
}
